import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { ReactElement } from 'react';
import { ICookiesPopupProps } from '../../components/common/CookiesPopUp';
import CallToAction, {
  ICallToActionProps,
} from '../../components/common/blocks/CallToAction';
import FaqCategories, {
  IFaqCategoriesProps,
} from '../../components/common/blocks/FaqCategories';
import { IFooterProps } from '../../components/common/layout/Footer';
import Layout from '../../components/common/layout/Layout';
import { INavBarProps } from '../../components/common/layout/NavBar';
import { IContactProps } from '../../components/contact-us/Contact';
import Hero, { IHeroComponentProps } from '../../components/home/Hero';
import { PageJSONLDSchema } from '../../models/app.models';
import {
  ICallToActionComponent,
  IContactMethod,
  IContactUsForm,
  ICustomerReviewsMeta,
  IFaqCategory,
  IFaqLandingPage,
  IFaqSubCategoryLandingPage,
  ILayoutPageProps,
} from '../../models/strapi.models';
import { fetchStrapi } from '../../services/api.service';
import { getRoutes } from '../../shared/useBuild';
import {
  buildApiKey,
  getCommonTranslations,
  getContactMethods,
  populateCookiesPopUpProps,
  populateFooterProps,
  populateNavBarProps,
  scrollToContactUs,
} from '../../shared/useCommon';
import {
  generateFaqsCategoryLandingPageSchema,
  generateOrganizationSchema,
  generateWebsiteSchema,
} from '../../shared/useJSONLDSchema';
import {
  flatStrapiCollection,
  flatStrapiObject,
  getAlternateLinks,
  getCanonicalLink,
  getContentLanguageCode,
  getTranslation,
  removeMarkDown,
} from '../../shared/useUtils';
import { IAppPage } from '../../store/slices/appSlice';
import { NextPageWithLayout } from '../_app';

export interface IFaqSubCategoryLandingPageProps extends ILayoutPageProps {
  hero: IHeroComponentProps;
  contactUsForm: IContactUsForm;
  contactUsFormSuccessMessage: string;
  contactUsFormErrorMessage: string;
  contactMethods: IContactMethod[];
  callToAction: ICallToActionComponent;
  faqCategories: IFaqCategoriesProps;
  alternativeLinks: { hrefLang: string; href: string }[];
  canonicalLink: string;
}

const FaqSubCategoryLandingPage: NextPageWithLayout<
  IFaqSubCategoryLandingPageProps
> = (props: IFaqSubCategoryLandingPageProps) => {
  const router = useRouter();
  const heroProps: IHeroComponentProps = {
    ...props.hero,
    buttonAction() {
      scrollToContactUs();
    },
  };

  const contactProps: IContactProps = {
    contactMethods: props.contactMethods.map((x) => ({
      type: x.type,
      label: getTranslation(x.labelTranslations, router.locale || 'en')
        .text as string,
      value:
        x.valueTranslations && x.valueTranslations.length > 0
          ? (getTranslation(x.valueTranslations, router.locale || 'en')
              .text as string)
          : undefined,
    })),
    titleTagName: 'h2',
    formLabels: {
      title: props.contactUsForm.title,
      subTitle: props.contactUsForm.subTitle,
      name: props.contactUsForm.nameLabel,
      email: props.contactUsForm.emailLabel,
      message: props.contactUsForm.messageLabel,
      submit: props.contactUsForm.submitLabel,
      submitSuccessMessage: props.contactUsFormSuccessMessage,
      submitErrorMessage: props.contactUsFormErrorMessage,
    },
  };

  const callToActionProps: ICallToActionProps = {
    title: props.callToAction.title,
    buttons: props.callToAction.buttons,
  };

  const ContactComponent = dynamic(
    () => import('../../components/contact-us/Contact')
  );

  return (
    <div className="page-container">
      <Head>
        <title>{props.meta.title}</title>
        <meta name="description" content={props.meta.description} />
        <meta name="keywords" content={props.meta.keywords} />
        <meta
          name="facebook-domain-verification"
          content="v1rp4jxjfkqwderk1ntxo8bjfy51hz"
        />
        <link rel="canonical" href={props.canonicalLink} />
        {props.alternativeLinks.map((x, i) => (
          <link key={i} rel="alternate" hrefLang={x.hrefLang} href={x.href} />
        ))}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(props.pageSchema.nodes),
          }}
        />
      </Head>

      <div className="page-content-section">
        <div className="page-content-block">
          <Hero {...heroProps} />
        </div>
      </div>

      <div className="page-content-section background-light">
        <div className="page-content-block">
          <FaqCategories {...props.faqCategories} />
        </div>
      </div>

      <div className="page-content-section is-narrower">
        <div className="page-content-block" id="contact">
          <ContactComponent {...contactProps} />
        </div>
      </div>

      <div className="page-content-section is-full-width">
        <div className="page-content-block" id="services">
          <CallToAction {...callToActionProps} />
        </div>
      </div>
    </div>
  );
};

FaqSubCategoryLandingPage.getLayout = function getLayout(page: ReactElement) {
  return (
    <Layout
      locale={page.props.locale}
      navBarProps={page.props.navBarProps}
      cookiesPopUpProps={page.props.cookiesPopUpProps}
      footerProps={page.props.footerProps}
      contentLanguageCode={getContentLanguageCode(page.props.locale)}>
      {page}
    </Layout>
  );
};

const entityBaseName = 'faq-category';

export async function getStaticPaths(context: any) {
  let paths: any[] = [];
  const landingsUrlParams = new URLSearchParams({
    'populate[0]': 'metas',
    'pagination[start]': '0',
    'pagination[limit]': '-1',
  });

  const landings: IFaqSubCategoryLandingPage[] = flatStrapiCollection(
    await fetchStrapi<any>(
      `/${entityBaseName}-landings`,
      landingsUrlParams,
      buildApiKey
    )
  );

  for (const locale of context.locales) {
    const localizedPaths = landings.map((item: IFaqSubCategoryLandingPage) => {
      const localizedMeta = getTranslation(item.metas, locale);
      return {
        params: {
          slug: localizedMeta.slug,
        },
        locale: locale,
      };
    });
    paths = [...paths, ...localizedPaths];
  }

  return { paths, fallback: false };
}

export async function getStaticProps(
  context: any
): Promise<{ props: IFaqSubCategoryLandingPageProps }> {
  const appRoutes = await getRoutes();
  const paramSlug = context.params.slug;

  const getLocalizedAppRoute = (actualPath: string) => {
    return (
      appRoutes?.find(
        (x: IAppPage) =>
          x &&
          x.destination.replace(`/${context.locale}`, '') === actualPath &&
          x.language === context.locale
      )?.source || actualPath
    );
  };

  const landingUrlParams = {
    'filters[metas][slug][$eq]': paramSlug,
    'populate[0]': 'metas',
    'populate[1]': 'hero',
    'populate[2]': 'hero.background',
    'populate[3]': 'contactUsForm',
    'populate[4]': 'contactUsFormSuccessMessage',
    'populate[5]': 'contactUsFormErrorMessage',
    'populate[6]': 'callToAction',
    'populate[7]': 'callToAction.buttons',
    'populate[8]': 'categoriesTitle',
    'populate[9]': 'faqCategories',
    'populate[10]': 'faqCategories.name',
    'populate[11]': 'faqCategories.subCategoryLandingPath',
    'populate[12]': 'faqCategories.icon',
    'populate[13]': 'faqCategories.faqLandings',
    'populate[14]': 'faqCategories.faqLandings.title',
    'populate[15]': 'faqCategories.faqLandings.metas',
    'populate[16]': 'faqCategories.subCategoryLandingButtonLabel',
    'populate[17]': 'breadcrumbAncestors',
  };

  // Get page data
  const landingsResult = await fetchStrapi<any>(
    `/${entityBaseName}-landings`,
    landingUrlParams,
    buildApiKey
  );
  const landing: IFaqSubCategoryLandingPage =
    flatStrapiCollection<IFaqSubCategoryLandingPage>(landingsResult)[0];

  if (!!landing.hero && landing.hero.length > 0) {
    landing.hero.forEach((x) => {
      if (!!(x.background as any).data) {
        x.background = flatStrapiObject(x.background);
      }
    });
  }

  const contactMethods = await getContactMethods();
  const navBarProps: INavBarProps = await populateNavBarProps(context.locale);
  const cookiesPopUpProps: ICookiesPopupProps = await populateCookiesPopUpProps(
    context.locale
  );
  const footerProps: IFooterProps = await populateFooterProps(context.locale);

  // Prepare Hero
  const heroData = getTranslation(landing.hero, context.locale);

  const heroProps: IHeroComponentProps = {
    title: heroData.title,
    description: heroData.description,
    buttonLabel: heroData.buttonLabel,
    buttonPath: getLocalizedAppRoute('/countries'),
    backgroundUrl:
      heroData.background?.formats?.xlarge?.url ||
      heroData.background?.url ||
      '/assets/images/home/map-image.png',
    titleHighlightedText: heroData.highlightedTitleText,
    backgroundAlt:
      flatStrapiObject<any>(heroData.background).alternativeText ||
      'Hero Background Image',
  };

  const meta = getTranslation(landing.metas, context.locale);
  const commonTranslations = await getCommonTranslations();
  const customerReviewsMeta = (
    await fetchStrapi<any>(`/customer-reviews-meta`, undefined, buildApiKey)
  ).data.attributes as ICustomerReviewsMeta;
  const websiteSchema = generateWebsiteSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string
  );
  const organizationSchema = await generateOrganizationSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string,
    customerReviewsMeta
  );

  const contactFormTitle = removeMarkDown(
    getTranslation(landing.contactUsForm, context.locale).title as string
  );
  const callToActionTitle = removeMarkDown(
    getTranslation(landing.callToAction, context.locale).title as string
  );
  const faqLandingUrlParams = {
    'populate[0]': 'metas',
    'pagination[page]': '1',
    'pagination[pageSize]': '15',
  };
  const faqLandingsResult = await fetchStrapi<any>(
    `/faq-landings`,
    faqLandingUrlParams,
    buildApiKey
  );
  const faqSubCategoryLandings: IFaqLandingPage[] =
    flatStrapiCollection<IFaqLandingPage>(faqLandingsResult);
  const faqsLandingPageSchema = generateFaqsCategoryLandingPageSchema(
    meta,
    contactFormTitle,
    callToActionTitle,
    faqSubCategoryLandings,
    getCanonicalLink(context.locale, meta.slug),
    context.locale
  );

  const pageSchema: PageJSONLDSchema = {
    nodes: [...websiteSchema, ...organizationSchema, ...faqsLandingPageSchema],
  };

  const originalPageMeta =
    landing.metas.find((x) => x.language === 'en') || landing.metas[0];
  const alternativeLinks = getAlternateLinks(
    `/faq-sub-categories/${originalPageMeta.slug}`,
    appRoutes
  );
  const canonicalLink = getCanonicalLink(context.locale || 'en', meta.slug);

  if (!!landing.faqCategories) {
    landing.faqCategories = flatStrapiCollection(landing.faqCategories as any);

    landing.faqCategories.forEach((category: IFaqCategory) => {
      category.icon = flatStrapiObject(category.icon);
      category.faqLandings = flatStrapiCollection(category.faqLandings as any);
    });
  }

  if (landing.breadcrumbAncestors && landing.breadcrumbAncestors.length > 0) {
    navBarProps.breadcrumbAncestors = landing.breadcrumbAncestors?.filter(
      (x) => x.language === context.locale
    );
  }

  // Populate breadcrumbs
  if (landing.breadcrumbAncestors && landing.breadcrumbAncestors.length > 0) {
    navBarProps.breadcrumbAncestors = landing.breadcrumbAncestors?.filter(
      (x) => x.language === context.locale
    );
  }

  navBarProps.breadcrumbsHomePageDisplayName = getTranslation(
    commonTranslations.homePageName,
    context.locale
  ).text as string;
  navBarProps.breadcrumbsCurrentPageDisplayName = heroProps.title;

  const faqCategoriesProps: IFaqCategoriesProps = {
    title: getTranslation(landing.categoriesTitle, context.locale)
      .text as string,
    categories: landing.faqCategories.map((category: IFaqCategory) => ({
      name: getTranslation(category.name, context.locale).text as string,
      path: getTranslation(category.subCategoryLandingPath, context.locale)
        .text as string,
      buttonLabel: getTranslation(
        category.subCategoryLandingButtonLabel,
        context.locale
      ).text as string,
      iconUrl:
        category.icon?.formats?.large?.url ||
        category.icon?.url ||
        '/assets/images/common/faq-icon.png',
      iconAlt: category.icon?.alternativeText || 'FAQ Category Icon',
      faqs: category.faqLandings.map((faq) => ({
        title: getTranslation(faq.title, context.locale).text as string,
        path: getTranslation(faq.metas, context.locale).slug as string,
      })),
    })),
  };

  const props: IFaqSubCategoryLandingPageProps = {
    // Layout
    pageSchema,
    navBarProps,
    cookiesPopUpProps,
    footerProps,
    // Page
    locale: context.locale,
    meta,
    hero: heroProps,
    contactMethods,
    contactUsForm: getTranslation(landing.contactUsForm, context.locale),
    contactUsFormSuccessMessage: getTranslation(
      landing.contactUsFormSuccessMessage,
      context.locale
    ).text as string,
    contactUsFormErrorMessage: getTranslation(
      landing.contactUsFormErrorMessage,
      context.locale
    ).text as string,
    callToAction: getTranslation(landing.callToAction, context.locale),
    faqCategories: faqCategoriesProps,
    alternativeLinks,
    canonicalLink,
  };

  return {
    props,
  };
}

export default FaqSubCategoryLandingPage;
